import httpRequest from "@/auth/http-client";
import { resolveContactSortApiField } from "@/helpers/contact-sort.helpers";
import { buildPaginationQuery, buildSortQuery } from "@/helpers/request-helpers";
import IContact, { IAddress, IAddressInput, IContactInput } from "@/interfaces/contact.interface";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

const DEFAULT_CONTACT_FILTER_SORT_BY = "firstName";
const DEFAULT_CONTACT_FILTER_SORT_DIRECTION = "asc";

class ContactService {
  private baseUrl = "api/v1/contact";
  private addressUrl = `${this.baseUrl}/address`;

  async getAll(pagination: RequestPagination): Promise<PaginatedResponse<IContact>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const contacts = await httpRequest.get(`${this.baseUrl}?${paginationQuery}`);
    return contacts;
  }

  async filter(
    filter: IFilter<IContact>[],
    pagination: RequestPagination,
    sortBy?: string | null,
    sortDirection?: string | null
  ): Promise<PaginatedResponse<IContact>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const resolvedSortBy = sortBy?.trim()
      ? resolveContactSortApiField(sortBy.trim())
      : DEFAULT_CONTACT_FILTER_SORT_BY;
    const resolvedSortDirection = sortDirection?.trim()
      ? sortDirection.trim()
      : DEFAULT_CONTACT_FILTER_SORT_DIRECTION;
    const sortQuery = buildSortQuery(resolvedSortBy, resolvedSortDirection);

    const response = await httpRequest.post(
      `${this.baseUrl}/filter?${paginationQuery}${sortQuery}`,
      {
        filter,
        sortBy: resolvedSortBy,
        sortDirection: resolvedSortDirection,
      }
    );

    return response;
  }

  async create(formData: IContactInput): Promise<void> {
    await httpRequest.post(this.baseUrl, formData);
  }

  async getById(contactId: number): Promise<IContact> {
    const contact = await httpRequest.get(`${this.baseUrl}/${contactId}`);

    return contact;
  }

  async update(formData: IContactInput, contactId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${contactId}`, formData);
  }

  async delete(contactId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${contactId}`);
  }

  //Addresses
  async getAllAddresses(pagination: RequestPagination): Promise<PaginatedResponse<IAddress>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const addresses = await httpRequest.get(`${this.addressUrl}?${paginationQuery}`);
    return addresses;
  }

  async createAddress(formData: IAddressInput): Promise<void> {
    await httpRequest.post(this.addressUrl, formData);
  }

  async getAddressById(addressId: number): Promise<IAddress> {
    const address = await httpRequest.get(`${this.addressUrl}/${addressId}`);

    return address;
  }

  async updateAddress(formData: IAddressInput, addressId: number): Promise<void> {
    await httpRequest.put(`${this.addressUrl}/${addressId}`, formData);
  }

  async deleteAddress(addressId: number): Promise<void> {
    await httpRequest.delete(`${this.addressUrl}/${addressId}`);
  }

  async importContactsFromFile(file: File): Promise<boolean> {
    const formData = new FormData();
    formData.append("File", file);

    try {
      await httpRequest.post(`${this.baseUrl}/import`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return true;
    } catch (error) {
      return false;
    }
  }
}

const contactService = new ContactService();
export default contactService;
